import React from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { Copy, Download, X as IconX } from 'lucide-react';
import { getMapTileAttribution, getMapTileUrl } from '../../config/mapTiles';

// --- ÍCONE ESPECÍFICO PARA ESTE MODAL ---
const duplicatePoleIcon = new L.DivIcon({
    html: `<svg viewBox="0 0 24 24" width="30" height="30" fill="#dc2626" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" stroke="white" stroke-width="1"/></svg>`,
    className: 'bg-transparent border-0',
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -30]
});

/**
 * @file Componente para o modal que exibe postes com protocolos duplicados em um mapa.
 *
 * @param {object} props - As propriedades do componente.
 * @param {boolean} props.isOpen - Controla a visibilidade do modal.
 * @param {function} props.onClose - Função para fechar o modal.
 * @param {Array} props.poles - Array de objetos contendo os postes duplicados com suas coordenadas e protocolos.
 * @param {string} props.theme - O tema atual da aplicação ('light' or 'dark').
 * @param {boolean} props.darkMapEnabled - Flag que indica se o mapa escuro está habilitado nas configurações.
 */
const DuplicatePolesModal = ({ isOpen, onClose, poles, theme, darkMapEnabled }) => {
    // Não renderiza nada se não estiver aberto ou se não houver postes para mostrar.
    if (!isOpen || !poles || poles.length === 0) {
        return null;
    }

    // Define o ponto central do mapa com base no primeiro poste da lista.
    const center = poles.length > 0 ? [poles[0].lat, poles[0].lon] : [-22.88, -48.44];
    
    // Define qual provedor de mapa (tile) usar com base no tema e na configuração.
    const isMapDark = theme === 'dark' && darkMapEnabled;
    const tileUrl = getMapTileUrl(isMapDark);
    const tileAttribution = getMapTileAttribution(isMapDark);

    /**
     * Gera e baixa um arquivo CSV com os dados dos postes duplicados.
     */
    const handleDownloadCSV = () => {
        if (!poles || poles.length === 0) return;

        const headers = ['Latitude', 'Longitude', 'Protocolos'];
        const csvRows = [
            headers.join(','),
            ...poles.map(pole => {
                const lat = pole.lat;
                const lon = pole.lon;
                // Coloca os protocolos entre aspas para garantir que a vírgula não quebre o CSV.
                const protocolsStr = `"${pole.protocols.join('; ')}"`;
                return [lat, lon, protocolsStr].join(',');
            })
        ];

        const csvContent = csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', 'postes_duplicados.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-[1050] flex justify-center items-center p-4" onClick={onClose}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <header className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                    <h2 className="text-xl font-bold flex items-center"><Copy className="mr-3 text-amber-500"/>Mapa de Postes com Duplicidade</h2>
                    <div className="flex items-center gap-x-2">
                        <button onClick={handleDownloadCSV} className="btn-secondary flex items-center">
                            <Download className="mr-2 h-4 w-4" />
                            Exportar CSV
                        </button>
                        <button onClick={onClose} aria-label="Fechar" title="Fechar" className="p-2 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2"><IconX /></button>
                    </div>
                </header>
                <div className="flex-grow p-2">
                    <MapContainer center={center} zoom={14} style={{ height: '100%', width: '100%' }} className={`rounded-lg ${isMapDark ? 'map-dark' : ''}`}>
                        <TileLayer url={tileUrl} attribution={tileAttribution} />
                        {poles.map((pole, idx) => (
                            <Marker key={idx} position={[pole.lat, pole.lon]} icon={duplicatePoleIcon}>
                                <Popup>
                                    <div className="text-sm">
                                        <b className="block mb-1">Protocolos:</b>
                                        <ul className="list-disc list-inside">
                                            {pole.protocols.map(p => <li key={p}>{p}</li>)}
                                        </ul>
                                    </div>
                                </Popup>
                            </Marker>
                        ))}
                    </MapContainer>
                </div>
            </div>
        </div>
    );
};

export default DuplicatePolesModal;
